import type {
  CampaignAnalyticsGranularity,
  CampaignAnalyticsResponse,
  SalesTrafficResponse,
} from "@adpilot/shared";
import { useTranslation } from "react-i18next";
import { ErrorState } from "@/components/shared/error-state";
import { buildDashboardPerformance, type DashboardPerformanceMetric } from "@/lib/dashboard-sales";
import { useReportingPeriodStore } from "@/stores/reporting-period";
import { useReportingTimeZoneStore } from "@/stores/reporting-time-zone";
import { DashboardFilterBar } from "./overview/dashboard-filter-bar";
import { KpiStrip, useDashboardKpiSlots } from "./overview/kpi-strip";
import type { DashboardCompareMode } from "./overview/kpi-strip-metrics";
import { PerformanceCard, usePerformanceChartPrefs } from "./overview/performance-card";

interface SourceFreshness {
  state: "current" | "pending";
  updatedAt: string | null;
  throughDate: string | null;
}

interface DashboardOverviewSectionsProps {
  analytics: CampaignAnalyticsResponse | undefined;
  salesTraffic?: SalesTrafficResponse;
  salesLoading?: boolean;
  salesFetching?: boolean;
  salesError?: boolean;
  onSalesRetry?: () => void;
  startDate: string;
  endDate: string;
  compare: DashboardCompareMode;
  onCompareChange: (compare: DashboardCompareMode) => void;
  /** Applied child ASINs of the filter bar's ASIN filter. */
  asins: string[];
  onAsinsChange: (asins: string[]) => void;
  granularity: CampaignAnalyticsGranularity;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  /** Inline CTA shown above the Sales retry when Ads is disconnected. */
  adsNotice?: React.ReactNode;
  /** Scope caveats shown beside the data freshness chip. */
  filterNotices?: React.ReactNode;
  /** Per-source freshness from `dashboard/data-freshness`; preferred over the report rows. */
  freshness?: { ads: SourceFreshness; sales: SourceFreshness };
  /** The freshness chip links to the data details in Settings. */
  canOpenDataDetails?: boolean;
  /** Card placed beside Performance (the AI status card in the Shop Dashboard design). */
  performanceAside?: React.ReactNode;
  /** Workspace target ACoS (fraction) for the Performance ACoS reference line. */
  targetAcos?: number | null;
}

/** Filter bar, KPI strip and the Performance chart over independently loaded Ads and Seller data. */
export function DashboardOverviewSections({
  analytics,
  salesTraffic,
  salesLoading = false,
  salesFetching = false,
  salesError = false,
  onSalesRetry,
  startDate,
  endDate,
  compare,
  onCompareChange,
  asins,
  onAsinsChange,
  granularity,
  isLoading,
  isFetching,
  isError,
  onRetry,
  adsNotice,
  filterNotices,
  freshness,
  canOpenDataDetails = false,
  performanceAside,
  targetAcos = null,
}: DashboardOverviewSectionsProps): React.ReactElement {
  const { t } = useTranslation("dashboard");
  const period = useReportingPeriodStore((state) => state.period);
  const setPeriod = useReportingPeriodStore((state) => state.setPeriod);
  const timeZone = useReportingTimeZoneStore((state) => state.timeZone);
  const setTimeZone = useReportingTimeZoneStore((state) => state.setTimeZone);
  const [slots, setSlots] = useDashboardKpiSlots();
  const performancePrefs = usePerformanceChartPrefs();
  const model = buildDashboardPerformance({
    analytics,
    salesTraffic,
    startDate,
    endDate,
    granularity,
  });
  const kpis = model.kpis.map((kpi) => ({
    ...kpi,
    loading:
      kpi.key === "totalSales" || kpi.key === "tacos" || kpi.key === "totalOrders"
        ? salesLoading
        : isLoading,
  }));
  const getLabel = (metric: DashboardPerformanceMetric) => t(`overview.kpi.metrics.${metric}`);

  // The earlier of the two sources is the date every number on the page covers. The
  // freshness endpoint knows when a source is still catching up; without it, fall back
  // to the report rows and the Seller sync runs.
  const earliest = (values: (string | null | undefined)[]) =>
    values.filter((value): value is string => Boolean(value)).sort()[0] ?? null;
  const dataThrough = freshness
    ? earliest([freshness.ads.throughDate, freshness.sales.throughDate])
    : earliest([analytics?.dataThrough, salesTraffic?.dataThrough]);
  const syncedAt = freshness
    ? earliest([freshness.ads.updatedAt, freshness.sales.updatedAt])
    : (salesTraffic?.shops
        .map((shop) => shop.lastSyncedAt)
        .filter((date): date is string => Boolean(date))
        .sort()
        .at(-1) ?? null);
  const syncing = freshness
    ? freshness.ads.state === "pending" || freshness.sales.state === "pending"
    : (salesTraffic?.shops.some(
        (shop) => shop.latestSync?.status === "queued" || shop.latestSync?.status === "processing",
      ) ?? false);

  // Sales and Ads load independently, and HTTP/schema failures get no toast, so the
  // chart keeps one compact retry per source.
  const adsErrorNotice = isError ? (
    <div role="alert">
      <ErrorState
        variant="inline"
        message={t("overview.performance.adsLoadFailed")}
        onRetry={onRetry}
        isRetrying={isFetching}
      />
    </div>
  ) : undefined;
  const salesNotice = salesError ? (
    <div role="alert">
      <ErrorState
        variant="inline"
        message={t("salesTraffic.loadFailed")}
        onRetry={onSalesRetry}
        isRetrying={salesFetching}
      />
    </div>
  ) : undefined;
  const notice =
    adsNotice || adsErrorNotice || salesNotice ? (
      <div className="space-y-2">
        {adsNotice}
        {adsErrorNotice}
        {salesNotice}
      </div>
    ) : undefined;

  return (
    <>
      <DashboardFilterBar
        dataThrough={dataThrough}
        syncedAt={syncedAt}
        syncing={syncing}
        linkToDataDetails={canOpenDataDetails}
        period={period}
        onPeriodChange={setPeriod}
        timeZone={timeZone}
        onTimeZoneChange={setTimeZone}
        compare={compare}
        onCompareChange={onCompareChange}
        asins={asins}
        onAsinsChange={onAsinsChange}
        notices={filterNotices}
      />
      <KpiStrip
        kpis={kpis}
        trend={model.trend}
        slots={slots}
        onSlotsChange={setSlots}
        compare={compare}
        getLabel={getLabel}
      />
      <div className="flex min-w-0 flex-col gap-4 xl:flex-row">
        <PerformanceCard
          className="flex-1"
          trend={model.trend}
          kpis={kpis}
          view={performancePrefs.view}
          onViewChange={performancePrefs.setView}
          lowerMetric={performancePrefs.lowerMetric}
          onLowerMetricChange={performancePrefs.setLowerMetric}
          targetAcos={targetAcos}
          granularity={granularity}
          getLabel={getLabel}
          isLoading={isLoading && salesLoading}
          notice={notice}
        />
        {performanceAside ? (
          <div className="flex min-w-0 shrink-0 xl:w-[380px]">{performanceAside}</div>
        ) : null}
      </div>
    </>
  );
}
